import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlineOutlined";
import {Button} from "@mui/material";
import type {AxiosError} from "axios";

interface ErrorFallbackProps {
    error: Error | AxiosError;
    is4xx?: boolean;
    showBaseNotice?: boolean;
}

const pad = (value: number): string => String(value).padStart(2, "0");

/** Formats a date as `yyyy-mm-dd hh:mm:ss` (ticket #55833). */
export const formatErrorDateTime = (date: Date): string =>
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ` +
    `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;

const ErrorFallback: React.FC<ErrorFallbackProps> = ({error, is4xx, showBaseNotice}: ErrorFallbackProps) => {
    const {t} = useTranslation();
    const [copied, setCopied] = useState(false);

    let subject = t("defaults.errorMail.subject") as string;
    subject = subject.concat(": ").concat(error.message);
    subject = encodeURIComponent(subject);
    const supportAddress = t("defaults.errorMail.href") as string;
    const href = encodeURIComponent(supportAddress);

    const dateTimeString = formatErrorDateTime(new Date());
    const currentUrl = window.location.href;
    const stackInfo =
        `${t("defaults.errorDetails.dateTime")}: ${dateTimeString}\n` +
        `URL: ${currentUrl}\n` +
        `${t("defaults.errorDetails.trace")}: ${error?.stack ?? error.message}`;
    const mailBody = encodeURIComponent(stackInfo);

    const link = `mailto:${href}?subject=${subject}&body=${mailBody}`;

    const handleMailClicked = () => {
        window.location.href = link;
    };

    const handleReload = () => {
        window.location.reload();
    };

    const handleBack = () => {
        window.history.back();
    };

    const handleCopy = async () => {
        await navigator.clipboard.writeText(stackInfo);
        setCopied(true);
        setTimeout(() => {
            setCopied(false);
        }, 1500);
    };

    return (
        <div className="flex flex-col items-center justify-center h-screen bg-transparent">
            <div className="px-6 py-4 rounded-sm w-full max-w-xl">
                <div className="aspect-square w-32 h-32 mx-auto mb-4">
                    <ErrorOutlineIcon className="w-full h-full text-an-error"/>
                </div>
                <div className="font-bold text-2xl mb-2 text-center text-an-error">
                    {t("defaults.error")}
                </div>
                {showBaseNotice &&
                    <div className="mb-4">
                        <p className="text-center">
                            {t("defaults.errorNotice")}{" "}
                            <a className="underline text-primary" href={link}>{supportAddress}</a>
                        </p>
                        <ul className="list-disc list-inside mt-2">
                            <li>{t("defaults.errorSupport.whatDone")}</li>
                            <li>{t("defaults.errorSupport.whichRecord")}</li>
                            <li>{t("defaults.errorSupport.whichStep")}</li>
                            <li>{t("defaults.errorSupport.screenshot")}</li>
                        </ul>
                    </div>
                }
                <div className="bg-an-panel p-4 mb-4 rounded-sm overflow-auto max-h-40">
                    <p className="font-bold text-sm mb-1">{t("defaults.errorDetails.title")}</p>
                    <p className="font-mono text-sm text-an-error">{error.message}</p>
                    <pre className="font-mono text-xs text-an-ink mt-2 whitespace-pre-wrap">{stackInfo}</pre>
                </div>
                <div className="flex justify-center space-x-4">
                    {!is4xx &&
                        <Button
                            className="bg-primary text-white px-4 py-2 rounded-sm hover:bg-primary-dark"
                            onClick={handleReload}
                        >
                            {t("defaults.reload")}
                        </Button>
                    }
                    <Button
                        className="bg-primary text-white px-4 py-2 rounded-sm hover:bg-primary-dark"
                        onClick={_e => {
                            void handleCopy();
                        }}
                    >
                        {copied ? t("defaults.copied") : t("defaults.copyToClipboard")}
                    </Button>
                    <Button
                        className="bg-primary text-white px-4 py-2 rounded-sm hover:bg-primary-dark"
                        onClick={handleBack}
                    >
                        {t("defaults.goBack")}
                    </Button>
                    <Button
                        className="bg-primary text-white px-4 py-2 rounded-sm hover:bg-primary-dark"
                        onClick={handleMailClicked}
                    >
                        {t("defaults.errorMail.button")}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default ErrorFallback;
